import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AssignmentOutlined as AssignmentIcon,
  SchoolOutlined as SchoolIcon,
  SettingsOutlined as SettingsIcon,
  BusinessOutlined as BusinessIcon,
  MenuBookOutlined as MenuBookIcon,
  LogoutOutlined as LogoutIcon,
  AssessmentOutlined as AssessmentIcon,
  /* Закомментированные иконки
  DescriptionOutlined as DescriptionIcon,
  SecurityOutlined as SecurityIcon,
  BusinessCenterOutlined as OrganizationIcon,
  */
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

interface SidebarProps {
  role: string;
  username: string;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ role, username, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAppsExpanded, setIsAppsExpanded] = useState(true);

  /* Временно закомментированные состояния
  const [isDocsExpanded, setIsDocsExpanded] = useState(false);
  const [isOrgsExpanded, setIsOrgsExpanded] = useState(false); // Состояние для Организаций
  */

  const normalizedRole = typeof role === 'string' ? role.toLowerCase() : (role as any)?.name?.toLowerCase() || 'user';
  const isAdmin = normalizedRole === 'admin';

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (paths: string[]) => paths.some(path => location.pathname.startsWith(path));

  const navItemClasses = (active: boolean) => `
    flex items-center justify-between px-4 py-3 mb-1 rounded-lg transition-colors duration-150 ease-in-out cursor-pointer
    select-none transform-gpu backface-hidden
    ${active ? 'bg-blue-50 text-[#1E61AF]' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
  `;

  const subMenuItemClasses = (active: boolean) => `
    text-sm py-2 px-3 rounded-md cursor-pointer transition-colors duration-150 ease-in-out
    select-none transform-gpu backface-hidden font-medium
    ${active ? 'text-[#1E61AF] bg-blue-50/50' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}
  `;

  return (
    <aside className="w-64 h-screen sticky top-0 bg-[#F9FAFB] border-r border-gray-200 flex flex-col p-4 overflow-y-auto shrink-0">
      {/* Заголовок */}
      <div
        className="flex items-center mb-10 px-2 cursor-pointer select-none"
        onClick={() => navigate('/applications')}
      >
        <span className="font-bold text-[#1E61AF] text-[1.15rem] tracking-tight">
            УПРАВЛЕНИЕ-СДО.РФ
        </span>
      </div>

      {/* Навигация */}
      <nav className="flex-1 space-y-1">
        <div>
          <div
            onClick={() => setIsAppsExpanded(!isAppsExpanded)}
            className={navItemClasses(isParentActive(['/application', '/drafts']))}
          >
            <div className="flex items-center gap-3">
              <AssignmentIcon fontSize="small" className="shrink-0" />
              <span className="font-medium text-sm">Заявки</span>
            </div>
            {isAppsExpanded ? <ExpandLess fontSize="small" className="shrink-0" /> : <ExpandMore fontSize="small" className="shrink-0" />}
          </div>

          {isAppsExpanded && (
            <div className="ml-9 space-y-1 mb-2">
              <div
                onClick={() => navigate('/applications')}
                className={subMenuItemClasses(isActive('/applications'))}
              >
                Список заявок
              </div>

              <div
                onClick={() => navigate('/application/new')}
                className={subMenuItemClasses(isActive('/application/new'))}
              >
                Новая заявка
              </div>

              <div
                onClick={() => navigate('/drafts')}
                className={subMenuItemClasses(isActive('/drafts'))}
              >
                Черновики заявок
              </div>
            </div>
          )}
        </div>

        <div onClick={() => navigate('/students')} className={navItemClasses(isActive('/students'))}>
          <div className="flex items-center gap-3">
            <SchoolIcon fontSize="small" className="shrink-0" />
            <span className="font-medium text-sm">Студенты</span>
          </div>
        </div>

        <div onClick={() => navigate('/monitoring')} className={navItemClasses(isActive('/monitoring'))}>
          <div className="flex items-center gap-3">
            <AssessmentIcon fontSize="small" className="shrink-0" />
            <span className="font-medium text-sm">Мониторинг</span>
          </div>
        </div>

        <div onClick={() => navigate('/programs')} className={navItemClasses(isActive('/programs'))}>
          <div className="flex items-center gap-3">
            <MenuBookIcon fontSize="small" className="shrink-0" />
            <span className="font-medium text-sm">Программы</span>
          </div>
        </div>

        <div onClick={() => navigate('/departments')} className={navItemClasses(isActive('/departments'))}>
          <div className="flex items-center gap-3">
            <BusinessIcon fontSize="small" className="shrink-0" />
            <span className="font-medium text-sm">Отделы</span>
          </div>
        </div>

        {/* Временно закомментировано: Организации с подвкладками */}
        {/*
        <div>
          <div
            onClick={() => setIsOrgsExpanded(!isOrgsExpanded)}
            className={navItemClasses(isParentActive(['/organizations']))}
          >
            <div className="flex items-center gap-3">
              <OrganizationIcon fontSize="small" className="shrink-0" />
              <span className="font-medium text-sm">Организации</span>
            </div>
            {isOrgsExpanded ? <ExpandLess fontSize="small" className="shrink-0" /> : <ExpandMore fontSize="small" className="shrink-0" />}
          </div>

          {isOrgsExpanded && (
            <div className="ml-9 space-y-1 mb-2">
              <div
                onClick={() => navigate('/organizations/customers')}
                className={subMenuItemClasses(isParentActive(['/organizations/customers']))}
              >
                Заказчики
              </div>
              <div
                onClick={() => navigate('/organizations/executors')}
                className={subMenuItemClasses(isParentActive(['/organizations/executors']))}
              >
                Исполнители
              </div>
            </div>
          )}
        </div>
        */}

        {isAdmin && (
          <>
            {/* Временно закомментировано: Управление доступом */}
            {/*
            <div onClick={() => navigate('/access')} className={navItemClasses(isActive('/access'))}>
              <div className="flex items-center gap-3">
                <SecurityIcon fontSize="small" className="shrink-0" />
                <span className="font-medium text-sm">Управление доступом</span>
              </div>
            </div>
            */}

            <div onClick={() => navigate('/settings')} className={navItemClasses(isActive('/settings'))}>
              <div className="flex items-center gap-3">
                <SettingsIcon fontSize="small" className="shrink-0" />
                <span className="font-medium text-sm">Настройки</span>
              </div>
            </div>

            {/* Временно закомментировано: Документы */}
            {/*
            <div>
              <div
                onClick={() => setIsDocsExpanded(!isDocsExpanded)}
                className={navItemClasses(isParentActive(['/documents']))}
              >
                <div className="flex items-center gap-3">
                  <DescriptionIcon fontSize="small" className="shrink-0" />
                  <span className="font-medium text-sm">Документы</span>
                </div>
                {isDocsExpanded ? <ExpandLess fontSize="small" className="shrink-0" /> : <ExpandMore fontSize="small" className="shrink-0" />}
              </div>

              {isDocsExpanded && (
                <div className="ml-9 space-y-1 mb-2">
                  <div
                    onClick={() => navigate('/documents/types')}
                    className={subMenuItemClasses(isActive('/documents/types'))}
                  >
                    Типы документов
                  </div>
                  <div
                    onClick={() => navigate('/documents/templates')}
                    className={subMenuItemClasses(isActive('/documents/templates') || isActive('/documents/templates/new') || location.pathname.includes('/mapping'))}
                  >
                    Шаблоны
                  </div>
                  <div
                    onClick={() => navigate('/documents/history')}
                    className={subMenuItemClasses(isActive('/documents/history'))}
                  >
                    Архив документов
                  </div>
                </div>
              )}
            </div>
            */}
          </>
        )}
      </nav>

      {/* Профиль и Выход */}
      <div className="mt-auto pt-4 border-t border-gray-200">
        <div className="flex items-center gap-3 px-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-[#1E61AF] font-bold text-xs shrink-0 uppercase">
            {username ? username[0] : '?'}
          </div>
          <div className="flex flex-col overflow-hidden">
            <span className="text-xs font-semibold text-gray-800 truncate">{username}</span>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">{normalizedRole}</span>
          </div>
        </div>

        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-150 text-sm font-medium transform-gpu backface-hidden"
        >
          <LogoutIcon fontSize="small" className="shrink-0" />
          <span>Выход</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;